import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation, untranslated } from "@i18n";
import IconComponent from "@CubeComponents/icons";
import Text from "@CubeComponents/html/Text/Text";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR } from "@Services/constants";
import {
  ExternalLinksHost,
  buildExternalLinkTarget,
  copyToClipboard,
  getExternalLinksForResource,
  permissionLabel,
  useExternalLinks,
  type ExternalLinkListItem,
} from "../../ExternalLinks";

/**
 * Details panel → "External Links" (PRD 3.8).
 *
 * Mirrors the Access section: a compact read of what is already true about this item, with the editing
 * behind one button. Creation is deliberately NOT offered here — it lives inside Manage, so there is one
 * create form reached from one place. Read-only for an internal viewer (PRD 2): the list renders for
 * anyone who can see the item and only Manage is withheld; `canManage` is the caller's call because the
 * file-manager row holds the permission object this panel does not.
 *
 * Only ACTIVE links are listed — a revoked link is history and belongs on the Manage modal's Revoked tab.
 *
 * The dialog flow is hosted HERE rather than shared with the File Manager's host: this panel is mounted
 * from four different trees (File Explorer, the viewer, FilesGrid, inspection forms), most of which have
 * no `useExternalLinks` above them, and only one panel is ever mounted at a time.
 */
type ExternalLinksContentProps = {
  /** Raw panel values; validated into a branded target below, or the section declines to render. */
  resourceId: string | undefined;
  spaceId: string | undefined;
  /** The item's current name, used as the default link label on create. */
  name: string | undefined;
  resourceType: string | undefined;
  /**
   * Whether this user may act on the links. Same proxy the context menu uses (`permission.share`):
   * the row carries no flag for full access, and the server remains the authority.
   */
  canManage: boolean;
};

function ExternalLinksContent({
  resourceId,
  spaceId,
  name,
  resourceType,
  canManage,
}: Readonly<ExternalLinksContentProps>) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const externalLinks = useExternalLinks();

  const [links, setLinks] = useState<ExternalLinkListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  /**
   * The validated target, or null when this row cannot carry a link at all. Built once and reused by
   * both the fetch and the Manage action so the two can never disagree about what is being shared.
   */
  const target = useMemo(
    () => buildExternalLinkTarget({ resourceId, spaceId, resourceType, name: name ?? "" }),
    [resourceId, spaceId, resourceType, name],
  );

  const load = useCallback(async () => {
    if (!target) {
      setLoading(false);
      return;
    }
    setLoading(true);
    // `includeRevoked` omitted: revoked rows are not fetched at all rather than fetched and filtered.
    const res = await getExternalLinksForResource({ resourceId: target.resourceId, spaceId: target.spaceId });
    // Silent on failure by design. This is one collapsible section among a dozen in a panel that
    // reloads on every selection change; a toast per failed background read would bury the user in
    // noise for something they never asked for. The Manage modal, which the user opens deliberately,
    // does report its errors.
    setLinks(res.status ? (res.links ?? []) : []);
    setLoading(false);
  }, [target]);

  // `linksChangedToken` rather than `refreshToken`: the latter also fires when a dialog merely
  // reopens, and re-reading the list for that would be a request per modal transition.
  useEffect(() => {
    // Fire-and-forget: `load` drives its own loading/links state (see its note on staying quiet).
    void load();
  }, [load, externalLinks.linksChangedToken]);

  const trimmedQuery = query.trim().toLowerCase();
  const visible = useMemo(
    () => (trimmedQuery ? links.filter((link) => link.name.toLowerCase().includes(trimmedQuery)) : links),
    [links, trimmedQuery],
  );

  const handleCopy = async (link: ExternalLinkListItem) => {
    const ok = await copyToClipboard(link.url);
    if (ok) {
      // Confirms in place on the row rather than via a toast — the control is 14px and the feedback
      // belongs where the user's eye already is.
      setCopiedId(link._id);
      return;
    }
    dispatch(showToastNotification({ content: t("Could not copy the link"), type: TOAST_TYPE_ERROR }));
  };

  /** Reset the tick on leave so the next hover starts from the copy icon again. */
  const handleLeave = () => {
    setHoveredId(null);
    setCopiedId(null);
  };

  const openManage = () => {
    if (target) externalLinks.openManage(target);
  };

  if (!target) {
    return (
      <Text className="block py-3 text-xs text-text_light">
        {t("External links can be created on files and folders only.")}
      </Text>
    );
  }

  return (
    <div className="flex flex-col gap-3 pb-2 pe-3">
      {/* Prototype layout (QA r2 #8): Manage sits on its OWN row under the section header, tinted
          like the Manage modal's "Create link" primary — not a plain button sharing a row with a
          count chip (the prototype carries no count here; the grid badge already answers "shared?"). */}
      {canManage && (
        <div>
          <button
            type="button"
            onClick={openManage}
            className="flex items-center gap-2 rounded-md border border-system_colour_1 bg-selection_primary px-3 py-1.5 text-sm font-semibold"
            data-testid="manageExternalLinksFromPanel"
          >
            <IconComponent name="editProfile" width="0.875rem" height="0.875rem" />
            <span>{t("Manage")}</span>
          </button>
        </div>
      )}

      {/* Shown only once there is something to filter: a search box above an empty list is noise. */}
      {links.length > 0 && (
        <div className="flex h-9 items-center gap-2 rounded-md border border-stroke_primary px-3">
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("Search")}
            aria-label={t("Search external links")}
            className="min-w-0 flex-1 border-0 bg-transparent text-xs outline-hidden"
          />
          <IconComponent name="search-icon" width="0.875rem" height="0.875rem" />
        </div>
      )}

      {loading && <Text className="block py-4 text-center text-xs text-text_light">{t("Loading...")}</Text>}

      {!loading && visible.length > 0 && (
        <div className="overflow-hidden rounded-md border border-stroke_primary">
          <div className="grid grid-cols-[minmax(0,1fr)_6.5rem] items-center border-b border-stroke_primary bg-light_background px-3 py-2 text-xs font-semibold text-text_light">
            <span>{t("Link Name")}</span>
            <span>{t("Permission")}</span>
          </div>
          {visible.map((link) => (
            <div
              key={link._id}
              onMouseEnter={() => setHoveredId(link._id)}
              onMouseLeave={handleLeave}
              className="grid grid-cols-[minmax(0,1fr)_6.5rem] items-center border-b border-stroke_primary px-3 py-2 text-xs last:border-b-0 hover:bg-card_hover_color"
            >
              <span className="flex min-w-0 items-center gap-2">
                <span className="truncate" title={link.name}>
                  {untranslated(link.name)}
                </span>
                {hoveredId === link._id && (
                  <button
                    type="button"
                    onClick={() => handleCopy(link)}
                    aria-label={t("Copy URL")}
                    title={t("Copy URL")}
                    className="flex-none"
                  >
                    <IconComponent
                      name={copiedId === link._id ? "TickRight" : "copy"}
                      width="0.875rem"
                      height="0.875rem"
                    />
                  </button>
                )}
              </span>
              <span className="text-text_light">{permissionLabel(t, link.permission)}</span>
            </div>
          ))}
        </div>
      )}

      {!loading && !visible.length && (
        <div className="rounded-md border border-dashed border-stroke_primary px-3 py-4 text-center text-xs leading-relaxed text-text_light">
          {/* Two distinct states: "nothing here" is a prompt to act, "nothing matches" is a prompt to
              clear the filter, and collapsing them would hide that links exist at all. */}
          {trimmedQuery
            ? t("No links match your search.")
            : canManage
              ? t("No external links yet. Use Manage to create one.")
              : t("No external links on this item.")}
        </div>
      )}

      <ExternalLinksHost state={externalLinks} />
    </div>
  );
}

export default ExternalLinksContent;
